import { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Activity, RefreshCw, Wifi, WifiOff, AlertTriangle, Clock, Tag, CheckCircle2 } from 'lucide-react';

const STATUS_CONFIG = {
  online: { color: '#22c55e', bg: 'rgba(34,197,94,0.1)', label: 'Online', icon: Wifi },
  not_found: { color: '#eab308', bg: 'rgba(234,179,8,0.1)', label: 'Not Found (404)', icon: AlertTriangle },
  timeout: { color: '#f97316', bg: 'rgba(249,115,22,0.1)', label: 'Timeout', icon: Clock },
  error: { color: '#ef4444', bg: 'rgba(239,68,68,0.1)', label: 'Error', icon: WifiOff },
  unknown: { color: '#6b7280', bg: 'rgba(107,114,128,0.1)', label: 'Unknown', icon: Activity },
};

export default function ProviderHealthPage() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [tagging, setTagging] = useState(false);

  useEffect(() => { fetchHealth(); }, []);

  const fetchHealth = async () => {
    try {
      const res = await api.get('/api/admin/provider-health');
      setProviders(res.data.providers || []);
    } catch {} finally { setLoading(false); }
  };

  const checkAll = async () => {
    setChecking(true);
    try {
      const res = await api.post('/api/admin/provider-health/check-all');
      setProviders(res.data.results || []);
      const s = res.data.summary || {};
      toast.success(`Health check done: ${s.online || 0} online, ${(s.total || 0) - (s.online || 0)} issues`);
    } catch (err) { toast.error(err.response?.data?.message || 'Health check failed'); }
    finally { setChecking(false); }
  };

  const autoTag = async () => {
    setTagging(true);
    try {
      const res = await api.post('/api/admin/provider-health/auto-tag');
      toast.success(`Tagged ${res.data.tagged || 0} games as "Manual Only"`);
    } catch (err) { toast.error('Auto-tag failed'); }
    finally { setTagging(false); }
  };

  const online = providers.filter(p => p.status === 'online').length;
  const offline = providers.length - online;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-5" data-testid="provider-health-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2"><Activity className="w-5 h-5 text-[#D4AF37]" /> Provider Health</h2>
          <p className="text-sm text-gray-400 mt-0.5">Monitor game provider API status and auto-tag unsupported providers</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={autoTag} disabled={tagging || offline === 0} variant="outline" className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10" data-testid="auto-tag-btn">
            {tagging ? <div className="spinner w-4 h-4 mr-2" /> : <Tag className="w-4 h-4 mr-2" />}
            Auto-Tag Unsupported
          </Button>
          <Button onClick={checkAll} disabled={checking} className="bg-[#D4AF37] text-black" data-testid="check-all-btn">
            {checking ? <div className="spinner w-4 h-4 mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Check All Providers
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-[#131822] rounded-xl border border-white/10 p-4">
          <p className="text-xs text-gray-500">Total Providers</p>
          <p className="text-2xl font-bold text-white">{providers.length}</p>
        </div>
        <div className="bg-[#131822] rounded-xl border border-white/10 p-4">
          <p className="text-xs text-gray-500">Online</p>
          <p className="text-2xl font-bold text-green-400">{online}</p>
        </div>
        <div className="bg-[#131822] rounded-xl border border-white/10 p-4">
          <p className="text-xs text-gray-500">Issues</p>
          <p className="text-2xl font-bold text-red-400">{offline}</p>
        </div>
      </div>

      {/* Provider Grid */}
      {providers.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <Activity className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No health data yet. Click "Check All Providers" to run the first health check.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {providers.map(p => {
            const cfg = STATUS_CONFIG[p.status] || STATUS_CONFIG.unknown;
            const Icon = cfg.icon;
            return (
              <div key={p.provider_code} className="bg-[#131822] rounded-xl border border-white/10 p-4" data-testid={`health-${p.provider_code}`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-white capitalize">{p.provider_name || p.provider_code}</h3>
                  <div className="flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium" style={{ background: cfg.bg, color: cfg.color }}>
                    <Icon className="w-3 h-3" />
                    {cfg.label}
                  </div>
                </div>
                <div className="space-y-1.5 text-xs">
                  <div className="flex justify-between"><span className="text-gray-500">Code</span><span className="text-gray-300 font-mono">{p.provider_code}</span></div>
                  {p.response_ms > 0 && <div className="flex justify-between"><span className="text-gray-500">Response</span><span className="text-gray-300">{p.response_ms}ms</span></div>}
                  {p.error && <div className="flex justify-between"><span className="text-gray-500">Error</span><span className="text-red-400 truncate max-w-[180px]">{p.error}</span></div>}
                  {p.checked_at && <div className="flex justify-between"><span className="text-gray-500">Checked</span><span className="text-gray-400">{new Date(p.checked_at).toLocaleString()}</span></div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
